import {connect} from 'react-redux';

import {buildContactsManagerContactsUrl} from '../../../../../../src/helpers/contactsManagerHelpers';
import {ResolveDispatchProps} from '../../../../../../src/helpers/reduxHelpers';
import {getUserNamespace} from '../../../../../../src/store/data/teammates/teammatesSelectors';
import {
  getContactsManagerSidePanelState,
  getContactsManagerTableState,
  makeGetContactsManagerParameters,
  makeGetContactsManagerSearchState,
} from '../../../../../../src/store/panels/contacts/contactsManager/contactsManagerPanelSelectors';
import {ContactsManagerParametersTypesEnum} from '../../../../../../src/store/panels/contacts/contactsManager/parameters/contactsManagerParameters';
import {contactsManagerSearchTablesForceReloadedAction} from '../../../../../../src/store/panels/contacts/contactsManager/search/contactsManagerSearchActions';
import {closeSidePanelAction} from '../../../../../../src/store/panels/contacts/contactsManager/sidePanel/contactsManagerSidePanelActions';
import {
  contactsManagerTableForceRefreshAction,
  contactsManagerTableRemoveCardIdRefsAction,
  contactsManagerTableRemoveSelectedCardIdsAction,
} from '../../../../../../src/store/panels/contacts/contactsManager/table/contactsManagerTableActions';
import {
  tableStateIsAccountContactsTableState,
  tableStateIsContactsTableState,
} from '../../../../../../src/store/panels/contacts/contactsManager/table/contactsManagerTableState';
import {ContactsManagerPanelId} from '../../../../../../src/store/panels/panelStateBase';
import {navigateToLinkAction} from '../../../../../../src/store/stateless/navigationActions';
import {
  makeMapDispatchToProps,
  makeMapStateToProps,
  ResolveStateProps,
} from '../../../../../../srcCommon/helpers/reselectHelpers';
import {ContactsManagerSidePanel} from './contactsManagerSidePanel';

/*
 * Props.
 */

interface ContactsManagerSidePanelContainerProps {
  panelId: ContactsManagerPanelId;
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<ContactsManagerSidePanelContainerProps>()(() => ({
  sidePanelState: getContactsManagerSidePanelState,
  tableState: getContactsManagerTableState,
  parameters: makeGetContactsManagerParameters(),
  searchState: makeGetContactsManagerSearchState(),
  namespace: getUserNamespace,
}));

const mapDispatchToProps = makeMapDispatchToProps<ContactsManagerSidePanelContainerProps>()(({panelId}) => ({
  closeSidePanel: closeSidePanelAction,
  removeSelectedCardIds: contactsManagerTableRemoveSelectedCardIdsAction,
  removeCardIds: (cardIds) => contactsManagerTableRemoveCardIdRefsAction(panelId, cardIds),
  navigateToLink: navigateToLinkAction,
  tableForceRefresh: contactsManagerTableForceRefreshAction,
  searchTablesForceReloaded: () => contactsManagerSearchTablesForceReloadedAction(panelId),
}));

function mergeProps(
  stateProps: ResolveStateProps<typeof mapStateToProps>,
  dispatchProps: ResolveDispatchProps<typeof mapDispatchToProps>,
) {
  const {tableState, parameters, searchState, namespace} = stateProps;
  const {removeSelectedCardIds, navigateToLink, removeCardIds} = dispatchProps;

  const maybeNavigateToBaseContactsRoute = () => {
    const cardGroupId =
      parameters.type === ContactsManagerParametersTypesEnum.LIST_CONTACTS
        ? parameters.cardGroupId
        : undefined;
    const accountId =
      parameters.type === ContactsManagerParametersTypesEnum.ACCOUNT_CONTACTS
        ? parameters.accountId
        : undefined;

    const baseContactsRoute = buildContactsManagerContactsUrl(parameters.type, {
      namespace,
      cardGroupId,
      accountId,
    });
    if (baseContactsRoute) {
      navigateToLink(baseContactsRoute);
    }
  };

  const onCloseContactDetails = () => {
    // With the contacts table, we support multi-select, so we should use the correct function.
    if (tableStateIsContactsTableState(tableState) || tableStateIsAccountContactsTableState(tableState)) {
      removeSelectedCardIds(tableState.selectedCardIds);
    }

    // If we are currently searching, we need to remove the selected card from the search table.
    if (parameters.type === ContactsManagerParametersTypesEnum.SEARCH) {
      removeSelectedCardIds(searchState?.contactsTableState?.selectedCardIds ?? []);
    }

    maybeNavigateToBaseContactsRoute();
  };

  const onCardDelete = (cardId: number) => {
    if (parameters.type !== ContactsManagerParametersTypesEnum.SEARCH) {
      maybeNavigateToBaseContactsRoute();
    }
    removeCardIds([cardId]);
  };

  return {
    ...stateProps,
    ...dispatchProps,
    onCloseContactDetails,
    onCardDelete,
  };
}

export const ContactsManagerSidePanelContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
  mergeProps,
)(ContactsManagerSidePanel);
